import { Link } from 'react-router-dom';
import { SectionHeading } from '../components';

const PrivacyPolicy = () => {
  return (
    <div className="bg-background">
      {/* ===== SECTION 1: PAGE HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-white">Legal</span>
          </span>
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Privacy Policy</h1>
          <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Your privacy matters. This policy explains how we collect, use, and protect your personal information.
          </p>
        </div>
      </section>

      {/* ===== LAST UPDATED ===== */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-slate-500">Last updated: March 2026</p>
        </div>
      </section>

      {/* ===== SECTION 2: INFORMATION WE COLLECT ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 1" title="Information We Collect" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              We collect information that you provide directly when using the Goldson-Wade Family Reunion website:
            </p>
            <ul className="space-y-3 text-slate-600 mt-4">
              <li><strong className="text-slate-900">Account information</strong> — name, email address, and phone number</li>
              <li><strong className="text-slate-900">Registration details</strong> — family member names, ages, dietary needs, accessibility requirements, and room accommodations</li>
              <li><strong className="text-slate-900">Payment information</strong> — processed securely through third-party payment gateways; we do not store full credit card numbers on our servers</li>
              <li><strong className="text-slate-900">Usage data</strong> — how you interact with the site, including pages visited and features used</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: HOW WE USE YOUR INFORMATION ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 2" title="How We Use Your Information" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              The information we collect is used exclusively for reunion planning and administration:
            </p>
            <ul className="space-y-3 text-slate-600 mt-4">
              <li>Process reunion registrations and payments</li>
              <li>Communicate about reunion logistics, schedules, and updates</li>
              <li>Manage house and room assignments and meal planning based on dietary needs</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send important notices about the reunion event</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              KG ProDesign LLC uses this information solely for reunion planning and administration purposes.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: PAYMENT DATA & KG PRODESIGN LLC ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 3" title="Payment Data & KG ProDesign LLC" />
          <div className="prose prose-slate max-w-none">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-4">
              <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3">Payment Processing Details</h4>
              <ul className="space-y-2 text-slate-600">
                <li>All payment processing is handled by <strong className="text-slate-900">KG ProDesign LLC</strong></li>
                <li>Payments will appear on your credit card statement as <strong className="text-slate-900">"KG PRODESIGN LLC"</strong> or <strong className="text-slate-900">"KGP"</strong></li>
                <li>KG ProDesign LLC manages all reunion funds and budget on behalf of the organizing committee</li>
                <li>Payment data is transmitted securely through encrypted payment gateways</li>
                <li>We do not store full credit card numbers on our servers</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: INFORMATION SHARING ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 4" title="Information Sharing" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              We do not sell or share your personal information with third parties for marketing purposes. Your information is shared only with:
            </p>
            <ul className="space-y-3 text-slate-600 mt-4">
              <li><strong className="text-slate-900">Reunion organizers</strong> — for event planning, house and room assignments, and meal coordination</li>
              <li><strong className="text-slate-900">Payment processors</strong> — for secure transaction processing through KG ProDesign LLC</li>
              <li><strong className="text-slate-900">Service providers</strong> — directly involved in reunion operations (venue, catering, transportation)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: DATA SECURITY ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 5" title="Data Security" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              We take reasonable measures to protect your personal information from unauthorized access, use, alteration, or destruction. This includes encrypted connections, secure data storage, and restricted access to personal data.
            </p>
            <p className="text-slate-600 leading-relaxed">
              While we strive to protect your information, no method of electronic storage or transmission is completely secure. We encourage you to use strong passwords and keep your login credentials confidential.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: YOUR RIGHTS ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 6" title="Your Rights" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              You have the right to access, correct, or request deletion of your personal data at any time. To exercise these rights, contact us using the information below and we will respond within a reasonable timeframe.
            </p>
            <p className="text-slate-600 leading-relaxed">
              You may also update your registration details, dietary preferences, and other personal information directly through your account dashboard at any time.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: CHILDREN'S PRIVACY ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 7" title="Children's Privacy" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              Information about minors (children under 18) is collected only as part of the family registration process by a parent or legal guardian. We do not knowingly collect personal information directly from children.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Parents and guardians who register minors are responsible for the accuracy of the information provided and for ensuring appropriate consent is obtained.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 9: CHANGES TO THIS POLICY ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 8" title="Changes to This Policy" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal and regulatory reasons. Updated versions will be posted on this page with a revised date.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We encourage you to review this policy periodically. For significant changes, we may notify registered users via email.
            </p>
          </div>
        </div>
      </section>

      {/* ===== SECTION 10: CONTACT US ===== */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Section 9" title="Contact Us" />
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed text-lg">
              If you have any questions about this Privacy Policy or how your data is handled, please contact us:
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 my-6">
              <p className="text-slate-900 font-bold text-lg mb-2">KG ProDesign LLC</p>
              <p className="text-slate-600">
                Website:{' '}
                <a href="https://kgprodesign.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  kgprodesign.com
                </a>
              </p>
              <p className="text-slate-600">
                Email:{' '}
                <a href="mailto:info@kgprodesign.com" className="text-primary hover:underline">
                  info@kgprodesign.com
                </a>
              </p>
              <p className="text-sm text-slate-500 mt-3">
                For payment-related inquiries, please reference "KG PRODESIGN LLC" or "KGP" as it appears on your credit card statement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CROSS-LINKS ===== */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/terms"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors"
            >
              Terms & Conditions
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
            <Link
              to="/cancellation-policy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors"
            >
              Cancellation & Refund Policy
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
